/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import './claim-entity-grid.scss';
import { Box, Stack, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import {
  CircularLoader,
  StatusBadge,
  CustomizedTable,
  createTextColumn,
  TableColumn,
} from '../../../presentation';
import {
  ClaimFilterInput,
  ClaimPaginationSort,
  ClaimSortKey,
  ClaimStatus,
  getPaginatedNodes,
  SortOrder,
  useClaimsQuery,
  useGetUsers,
  UserGroup,
  useUpsertClaimMutation,
} from '@converge/graphql';

import { ConvergeNavigationUrls } from '../../../contexts';
import { displayFormatDateUTC } from '@converge/utils';
import { CheckedState } from '../../../presentation/components/table-filter';
import React, { useEffect, useState } from 'react';
import ExpandedTaskRow, {
  flexValuesClaimsExpanded,
} from './components/expanded-task-row';
import { isDateOverdue } from './utils/date-utils';

import ClaimsFilter, { ClaimFilterOptions } from './claim-filter';
import { AssigneeDropdown } from './components/assignee-dropdown';
import { getUserOptions } from '../../submission/underwriting-queue-grid';
import { useConvergeSnackBar } from '../../../contexts';
import { FormProvider, useForm } from 'react-hook-form';
import { useCurrentUserUuid } from '../../auth/auth-context';

export interface ClaimEntityDetails {
  uuid: string;
  referenceId: string;
  legalName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  createdAt: string;
  updatedAt: string;
  revenue: number;
  naicsCode: string | number;
  documentsLink: string;
  industrySector: string;
}

const cleanFilters = (
  filters: any,
  isAllClaims: boolean,
  currentUserUuid?: string | null
): ClaimFilterInput => {
  const result: ClaimFilterInput = {};

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      switch (key) {
        case 'claimNumber': {
          result.referenceId = value[0];
          break;
        }
        case 'status': {
          // Map status values to ClaimStatus enum
          const statuses = value
            .map((statusValue: string) => {
              const upperStatus = statusValue.toUpperCase();
              if (upperStatus === 'OPEN') {
                return ClaimStatus.Open;
              } else if (upperStatus === 'REOPENED') {
                return ClaimStatus.Reopened;
              } else if (upperStatus === 'CLOSED') {
                return ClaimStatus.Closed;
              }
              return null;
            })
            .filter(Boolean);

          if (statuses.length > 0) {
            result.statuses = statuses as any;
          }
          break;
        }
        case 'brokerageUuids': {
          result.brokerageUuids = value;
          break;
        }
        case 'claimHandler': {
          result.claimHandlerUuids = value;
          break;
        }
        case 'insured': {
          result.insuredUuids = value;
          break;
        }
        case 'claimant': {
          result.claimantUuids = value;
          break;
        }
        case 'policy': {
          result.policyUuids = value;
          break;
        }
        case 'loss_date': {
          try {
            if (value[0].startsWith('{')) {
              const dateRange = JSON.parse(value[0]);
              if (dateRange.start && dateRange.end) {
                result.lossDate = {
                  start: dateRange.start,
                  end: dateRange.end,
                };
              }
            }
          } catch (e) {
            console.error('Error parsing loss_date filter:', e);
          }
          break;
        }
        case 'reportedDate': {
          try {
            if (value[0].startsWith('{')) {
              const dateRange = JSON.parse(value[0]);
              // Send to API if at least one date is selected
              if (dateRange.start || dateRange.end) {
                result.reportedDate = {
                  start: dateRange.start,
                  end: dateRange.end,
                };
              }
            }
          } catch (e) {
            console.error('Error parsing reportedDate filter:', e);
          }
          break;
        }
        case 'dueDate': {
          try {
            if (value[0].startsWith('{')) {
              const dateRange = JSON.parse(value[0]);
              // Send to API if at least one date is selected
              if (dateRange.start || dateRange.end) {
                result.dueDate = {
                  start: dateRange.start,
                  end: dateRange.end,
                };
              }
            }
          } catch (e) {
            console.error('Error parsing dueDate filter:', e);
          }
          break;
        }

        default: {
          break;
        }
      }
    }
  });

  if (!isAllClaims) {
    // Always filter by OPEN and REOPENED statuses in Claim Queue view
    if (!result.statuses || result.statuses.length === 0) {
      result.statuses = [ClaimStatus.Open, ClaimStatus.Reopened] as any;
    }
    if (currentUserUuid) {
      result.assignedTaskUserUuids = [currentUserUuid];
    }
    result.dueTasks = true;
  }
  return result;
};

export interface ClaimEntityTableProps {
  isAllClaims?: boolean;
}

// Component for claim handler field with click-to-edit functionality
export const ClaimHandlerField: React.FC<{
  claimUuid: string;
  currentHandler: string;
  userUuid?: string;
}> = ({ claimUuid, currentHandler, userUuid }) => {
  const [selectedUserUuid, setSelectedUserUuid] = React.useState(
    userUuid || ''
  );
  const { notify } = useConvergeSnackBar();

  // Fetch users from the Claims group
  const { data: claimUsersList } = useGetUsers({
    userGroups: [UserGroup.Claims],
  });

  // Initialize the upsert claim mutation
  const { upsertClaim } = useUpsertClaimMutation({
    shouldRefetch: true,
    onError: (error: any) => {
      console.error('Failed to update claim handler:', error);
      notify(
        `Failed to update claim handler: ${error.message || 'Unknown error'}`,
        {
          variant: 'error',
        }
      );
    },
  });

  // Convert user data to options format for dropdown
  const assigneeOptions = React.useMemo(() => {
    return getUserOptions(claimUsersList, false) || [];
  }, [claimUsersList]);

  // Create form methods for the dropdown
  const formMethods = useForm({
    defaultValues: {
      [`assignee-${claimUuid}`]: selectedUserUuid,
    },
  });

  // Keep selectedUserUuid in sync with props
  React.useEffect(() => {
    setSelectedUserUuid(userUuid || '');
  }, [userUuid]);

  // Keep form state in sync with selectedUserUuid
  React.useEffect(() => {
    formMethods.setValue(`assignee-${claimUuid}`, selectedUserUuid, {
      shouldDirty: false,
    });
  }, [selectedUserUuid, claimUuid, formMethods]);

  const handleUpdateClaimHandler = React.useCallback(
    async (event: any) => {
      const newUserUuid = event?.target?.value as string | null;

      formMethods.setValue(`assignee-${claimUuid}`, newUserUuid || '', {
        shouldDirty: false,
        shouldValidate: true,
      });

      // Update local state for consistency
      setSelectedUserUuid(newUserUuid || '');

      notify(
        newUserUuid && newUserUuid !== ''
          ? 'Updating claim handler...'
          : 'Removing claim handler...',
        {
          variant: 'info',
        }
      );

      try {
        await upsertClaim({
          variables: {
            claimInput: {
              claimUuid: claimUuid,
              userUuid: newUserUuid === '' ? null : newUserUuid, // Convert empty string to null for backend
            },
          },
        });

        notify(
          newUserUuid && newUserUuid !== ''
            ? 'Claim handler updated successfully'
            : 'Claim handler removed successfully',
          {
            variant: 'success',
          }
        );
      } catch (e) {
        console.error('Error updating claim handler:', e);

        // Revert both form state and local state on error
        const originalValue = userUuid || '';
        formMethods.setValue(`assignee-${claimUuid}`, originalValue, {
          shouldDirty: false,
        });
        setSelectedUserUuid(originalValue);

        notify(
          `Error updating claim handler: ${
            e instanceof Error ? e.message : 'Unknown error'
          }`,
          {
            variant: 'error',
          }
        );
      }
    },
    [upsertClaim, claimUuid, notify, userUuid, formMethods]
  );

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ minWidth: '170px', maxWidth: '170px', marginBottom: '26px' }}>
        <AssigneeDropdown
          taskId={claimUuid}
          options={assigneeOptions}
          hideBorders={false}
          disabled={false}
          onUpdateTaskOwner={handleUpdateClaimHandler}
        />
      </Box>
    </FormProvider>
  );
};

export const ClaimEntityTable: React.FC<ClaimEntityTableProps> = ({
  isAllClaims = true,
}) => {
  const currentUserUuid = useCurrentUserUuid();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get responsive text lengths based on window width
  const getTextLengths = () => {
    if (windowWidth >= 1536) {
      return { insured: 35, claimant: 28 }; // XL screens - show more text
    } else if (windowWidth >= 1200) {
      return { insured: 25, claimant: 18 }; // Large screens
    } else if (windowWidth >= 900) {
      return { insured: 18, claimant: 12 }; // Medium screens
    } else {
      return { insured: 15, claimant: 10 }; // Very small screens
    }
  };

  const textLengths = getTextLengths();

  const localStoragePageSize: number = parseInt(
    localStorage.getItem('claimPageSize') ?? '0'
  );
  const pageNumberKey = isAllClaims
    ? 'claimPageNumber'
    : 'currentUserclaimPageNumber';
  const localStoragePageNumber: number = parseInt(
    localStorage.getItem(pageNumberKey) ?? '0'
  );

  const [currentPage, setCurrentPage] = useState<number>(
    localStoragePageNumber || 0
  );
  const [pageSize, setPageSize] = useState<number>(localStoragePageSize || 10);

  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<CheckedState>({
    claimNumber: [],
    insuredUuid: [],
    claimant: [],
    claimHandler: [],
    status: [],
    brokerageUuids: [],
    loss_date: [],
    reportedDate: [],
  });
  const [currentFilterLabels, setCurrentFilterLabels] = useState<CheckedState>({
    claimNumber: [],
    insured: [],
    claimant: [],
    claimHandler: [],
    status: [],
    brokerageUuids: [],
    loss_date: [],
    reportedDate: [],
  });
  const [filterLabels, setFilterLabels] = useState<CheckedState>({
    claimNumber: [],
    insured: [],
    claimant: [],
    claimHandler: [],
    status: [],
    brokerageUuids: [],
    loss_date: [],
    reportedDate: [],
  });

  // Local storage utilities for claim sort - use different keys for different views
  const STORAGE_KEY = isAllClaims ? 'all-claims-sort' : 'my-claims-sort';

  const getStoredSort = (): ClaimPaginationSort => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to parse stored claim sort:', error);
    }
    // Default sort by reported_date desc for all views
    return {
      key: ClaimSortKey.ReportedDate,
      order: SortOrder.Desc,
    };
  };

  const setStoredSort = (sort: ClaimPaginationSort) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
    } catch (error) {
      console.warn('Failed to store claim sort:', error);
    }
  };

  // Initialize sort from storage
  const [currentClaimSort, setCurrentClaimSort] =
    useState<ClaimPaginationSort>(getStoredSort);

  // State for filter options derived from claims data
  const [dynamicFilterOptions, setDynamicFilterOptions] =
    useState<ClaimFilterOptions>({
      claimNumber: [],
      insured: [],
      claimant: [],
      claimHandler: [],
      status: [],
      brokerageUuids: [],
      loss_date: [],
      reportedDate: [],
      dueDate: [],
    });

  // Load saved filters from localStorage if they exist - separate keys for All Claims and Claim Queue
  useEffect(() => {
    const filterKey = isAllClaims ? 'allClaimsFilters' : 'claimQueueFilters';
    const labelKey = isAllClaims
      ? 'allClaimsFilterLabels'
      : 'claimQueueFilterLabels';

    const savedFilters = localStorage.getItem(filterKey);
    const savedFilterLabels = localStorage.getItem(labelKey);

    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);

        // If savedFilters contains the API format with filters.referenceId
        if (parsedFilters.filters && parsedFilters.filters.referenceId) {
          setCurrentFilters({
            ...currentFilters,
            claimNumber: [parsedFilters.filters.referenceId],
          });
        } else {
          // Regular format
          setCurrentFilters(parsedFilters);
        }
      } catch (error) {
        console.error('Error parsing saved claim filters', error);
      }
    } else {
      // Reset to default filters when switching views if no saved filters exist
      setCurrentFilters({
        claimNumber: [],
        insured: [],
        claimant: [],
        claimHandler: [],
        status: [],
        brokerageUuids: [],
        loss_date: [],
        reportedDate: [],
      });
    }

    if (savedFilterLabels) {
      try {
        const parsedLabels = JSON.parse(savedFilterLabels);
        setCurrentFilterLabels(parsedLabels);
        setFilterLabels(parsedLabels);
      } catch (error) {
        console.error('Error parsing saved claim filter labels', error);
      }
    } else {
      // Reset to default labels when switching views if no saved labels exist
      setCurrentFilterLabels({
        claimNumber: [],
        insured: [],
        claimant: [],
        claimHandler: [],
        status: [],
        brokerageUuids: [],
        loss_date: [],
        reportedDate: [],
      });
      setFilterLabels({
        claimNumber: [],
        insured: [],
        claimant: [],
        claimHandler: [],
        status: [],
        brokerageUuids: [],
        loss_date: [],
        reportedDate: [],
      });
    }

    // If we have referenceId in savedFilters but no corresponding label, add it
    if (
      savedFilters &&
      (!savedFilterLabels || !JSON.parse(savedFilterLabels || '{}').claimNumber)
    ) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        if (parsedFilters.filters && parsedFilters.filters.referenceId) {
          const referenceId = parsedFilters.filters.referenceId;
          setFilterLabels((prev) => ({
            ...prev,
            claimNumber: [referenceId],
          }));
          setCurrentFilterLabels((prev) => ({
            ...prev,
            claimNumber: [referenceId],
          }));
        }
      } catch (error) {
        console.error('Error setting up filter labels for referenceId', error);
      }
    }
  }, [isAllClaims]);

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const resetPageNumber = () => {
    setCurrentPage(0);
    localStorage.setItem(pageNumberKey, '0');
  };

  const setClaimFilters = (filters: CheckedState) => {
    // Make sure the filterLabels are properly updated when filters are applied
    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key]?.length === 0) {
          setFilterLabels((prev) => ({
            ...prev,
            [key]: [],
          }));
        }
      });

      // Store in localStorage with separate keys for All Claims and Claim Queue
      const filterKey = isAllClaims ? 'allClaimsFilters' : 'claimQueueFilters';
      localStorage.setItem(filterKey, JSON.stringify(filters));
    }

    // Force a re-render to apply the filter
    setCurrentPage(currentPage);
  };

  // Format filters for API - use separate keys for All Claims and Claim Queue
  const filterKey = isAllClaims ? 'allClaimsFilters' : 'claimQueueFilters';
  const savedFilters = localStorage.getItem(filterKey);
  let filtersToUse = currentFilters;

  // If savedFilters contains API format data, extract and convert to our format
  if (savedFilters) {
    try {
      const parsedFilters =
        savedFilters && savedFilters !== '' ? JSON.parse(savedFilters) : {};

      if (parsedFilters && typeof parsedFilters === 'object') {
        if (parsedFilters.filters && parsedFilters.filters.referenceId) {
          // API format with filters.referenceId
          filtersToUse = {
            ...currentFilters,
            claimNumber: [parsedFilters.filters.referenceId],
          };
        } else if (Object.keys(parsedFilters).length > 0) {
          // Our component format
          filtersToUse = parsedFilters;
        }
      }
    } catch (error) {
      console.error('Error parsing saved filters for API', error);
    }
  }

  const filteredQueryParams = cleanFilters(
    filtersToUse,
    isAllClaims,
    currentUserUuid
  );

  // Use server-side filtering with the API
  const { loading, data } = useClaimsQuery({
    pageNumber: currentPage,
    pageSize: pageSize,
    filters:
      Object.keys(filteredQueryParams).length > 0
        ? filteredQueryParams
        : undefined,
    includeTasks: !isAllClaims,
    sort: currentClaimSort,
  });

  // We're now using server-filtered claims
  const claims = React.useMemo(() => {
    const claimsToUse = getPaginatedNodes(data?.claims);

    return claimsToUse?.map((claim: any, index: number) => {
      const claimId = claim.uuid;

      const claimantContact = claim?.claimants?.find(
        (contact: any) => contact?.type === 'CLAIMANT'
      );

      const claimant = claimantContact?.nameSearch || '';

      // Extract claim handler information from user field
      const claimHandler = claim?.user
        ? `${claim.user.firstName} ${claim.user.lastName}`
        : '—';
      const claimHandlerUuid = claim?.user?.uuid;

      // Extract tasks from the claim
      const claimTasks =
        claim?.tasks?.edges?.map((taskEdge: any) => {
          const task = taskEdge.node;
          const taskUser = task?.user?.node;

          return {
            id: task?.uuid || '',
            uuid: task?.uuid || '',
            taskName: task?.label || task?.targetName || '—',
            status: task?.status || 'OPEN',
            dueDate: task?.dueDate || '',
            priority: task?.priority || '',
            type: task?.type || '',
            note: task?.note || '',
            assignedUser: taskUser
              ? `${taskUser.firstName || ''} ${taskUser.lastName || ''}`.trim()
              : '—',
            userUuid: taskUser?.uuid || '',
          };
        }) || [];

      return {
        id: claimId,
        uuid: claim?.uuid || '',
        referenceId: claim?.referenceId || '',
        insured:
          claim?.insuredEntity?.node?.aliases?.edges[0]?.node?.name || '—',
        insuredUuid: claim?.insuredEntity?.node?.uuid || '',
        claimant: claimant || '—',
        claimantUuid: claimantContact?.uuid || '',
        reportedDate: claim?.reportedDate || '—',
        lossDate: claim?.lossDate || '-',
        claimHandler: claimHandler,
        claimHandlerUuid: claimHandlerUuid,
        brokerageName: (() => {
          const name =
            claim?.brokerEntity?.node?.aliases?.edges?.[0]?.node?.name ||
            claim?.quote?.node?.brokerage?.name ||
            claim?.brokerage?.name ||
            claim?.insuredEntity?.node?.brokerage?.name ||
            '-';
          return name;
        })(),
        brokerageUuid:
          claim?.brokerEntity?.node?.uuid ||
          claim?.quote?.node?.brokerage?.uuid ||
          claim?.brokerage?.uuid ||
          claim?.insuredEntity?.node?.brokerage?.uuid ||
          '',
        status: claim?.status || 'OPEN',
        dueDate: claim.dueDate || '—',
        policyReferenceId:
          claim?.quote?.node?.policies?.edges[0]?.node?.referenceId || '—',
        createdAt: claim?.createdAt || '',
        documentLink: claim?.documentLink || '',
        tasks: claimTasks,
        tasksCount: claimTasks?.length || 0,
        dateOfLoss: claim?.lossDate || '',
      };
    });
  }, [data?.claims]);

  // Extract unique claim numbers and insured names from the claims data
  useEffect(() => {
    if (claims && Array.isArray(claims) && claims.length > 0) {
      // Extract unique claim numbers
      const uniqueClaimNumbers = Array.from(
        new Set(claims.map((claim) => claim.referenceId))
      )
        .filter(Boolean)
        .map((referenceId) => ({
          label: referenceId as string,
          value: referenceId as string,
        }));

      const insuredMap = new Map<string, string>();
      claims.forEach((claim) => {
        if (claim.insuredUuid && claim.insured && claim.insured !== '—') {
          insuredMap.set(claim.insuredUuid, claim.insured);
        }
      });
      const uniqueInsureds = Array.from(insuredMap.entries()).map(
        ([uuid, name]) => ({
          label: name,
          value: uuid,
        })
      );

      const claimantMap = new Map<string, string>();
      claims.forEach((claim) => {
        if (claim.claimantUuid && claim.claimant && claim.claimant !== '—') {
          claimantMap.set(claim.claimantUuid, claim.claimant);
        }
      });
      const uniqueClaimants = Array.from(claimantMap.entries()).map(
        ([uuid, name]) => ({
          label: name,
          value: uuid,
        })
      );

      const uniqueClaimHandlers = Array.from(
        new Set(claims.map((claim) => claim.claimHandler))
      )
        .filter(Boolean)
        .map((claimHandler) => ({
          label: claimHandler as string,
          value: claimHandler as string,
        }));

      // Note: Brokerages are now fetched from API in claim-filter.tsx
      // We don't extract them from claims to ensure ALL brokerages are available in the filter

      // Update filter options (excluding brokerageUuids - handled by API in filter component)
      setDynamicFilterOptions((prevOptions) => ({
        ...prevOptions,
        claimNumber: uniqueClaimNumbers,
        insured: uniqueInsureds,
        claimant: uniqueClaimants,
        claimHandler: uniqueClaimHandlers,
        // brokerageUuids is not set here - it comes from the API in the filter component
      }));
    }
  }, [claims, isAllClaims]);

  if (loading) {
    return (
      <div className="claims-loader">
        <CircularLoader className="circular-loader-style" />;
      </div>
    );
  }

  const renderTableCell = (value: string, isStatus?: boolean) => {
    if (isStatus) {
      let className = 'inactive-claim';
      if (value === 'Open' || value === 'Reopen') {
        className = 'active-claim';
      } else if (value === 'Reopened') {
        className = 'reopened-claim';
      } else if (value === 'Closed' || value === 'CLOSED') {
        className = 'closed-claim';
      }

      return (
        <Typography className={`table-cell ${className}`}>{value}</Typography>
      );
    }
    return <Typography className="table-cell">{value}</Typography>;
  };

  interface ClaimRowData {
    id: string;
    uuid: string;
    referenceId: string;
    insured: string;
    claimant: string;
    claimHandler: string;
    claimHandlerUuid: string;
    brokerageName: string;
    status: string;
    tasksCount: number;
    createdAt: string;
    reportedDate: string;
    lossDate: string;
    dueDate: string;
    tasks: any[];
    [key: string]: unknown; // Index signature for flexibility
  }

  const tasksCountColumn = createTextColumn<ClaimRowData>(
    'tasksCount',
    'PENDING TASKS',
    {
      sortable: true,
      flex: flexValuesClaimsExpanded[6],
      render: (value, row) => {
        // Check if main claim due date is overdue
        const isMainDueDateOverdue = isDateOverdue(
          row.dueDate,
          'main-row-badge-claim-due'
        );

        // Check if any task due dates are overdue
        const hasOverdueTaskDates =
          row.tasks && Array.isArray(row.tasks)
            ? row.tasks.some((task: any) =>
                isDateOverdue(task?.dueDate, 'main-row-badge-task-due')
              )
            : false;

        // Badge is red if EITHER main due date OR any task due date is overdue
        const hasOverdueTasks = isMainDueDateOverdue || hasOverdueTaskDates;

        return (
          <Stack className="status-badge">
            <StatusBadge
              label={String(value || 0)}
              color={hasOverdueTasks ? '#D32F2F' : '#2E7D32'}
              backgroundColor={hasOverdueTasks ? '#FDEDED' : '#EDF7ED'}
              sx={{
                minWidth: '24px',
                height: '24px',
                borderRadius: '50%',
                '& .MuiChip-label': {
                  padding: '0',
                  fontWeight: '600',
                  fontSize: '12px',
                },
              }}
            />
          </Stack>
        );
      },
    }
  );

  // Define the status column
  const statusColumn = createTextColumn<ClaimRowData>('status', 'STATUS', {
    sortable: true,
    flex: 1,
    align: 'center',
    render: (value) => {
      let displayValue = 'Closed';
      let color = '#D32F2F';
      let backgroundColor = '#FDEDED';

      if (value === 'OPEN') {
        displayValue = 'Open';
        color = '#2E7D32';
        backgroundColor = '#EDF7ED';
      } else if (value === 'REOPEN') {
        displayValue = 'Reopen';
        color = '#2E7D32';
        backgroundColor = '#EDF7ED';
      } else if (value === 'REOPENED') {
        displayValue = 'Reopened';
        color = '#0288D1';
        backgroundColor = '#E5F6FD';
      }

      return (
        <div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '3px 24px',
              borderRadius: '16px',
              border: `1px solid ${color}`,
              backgroundColor: `${backgroundColor}`,
              fontSize: '12px',
              fontWeight: '500',
              color: color,
              width: '65px',
              justifyContent: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {displayValue || '—'}
          </span>
        </div>
      );
    },
  });

  // Define broker column for All Claims view
  const brokerageColumn = createTextColumn<ClaimRowData>(
    'brokerageName',
    'BROKERAGE',
    {
      sortable: true,
      align: 'left',
      flex: 1.0,
      render: (value) => {
        const brokerageName = String(value ?? '\u2014');
        const truncatedName =
          brokerageName.length > 16
            ? brokerageName.substring(0, 16) + '...'
            : brokerageName;
        return (
          <Typography
            className="table-cell"
            title={brokerageName}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {truncatedName}
          </Typography>
        );
      },
    }
  );

  // Handle sort change from table with three-state cycle: asc -> desc -> default (ReportedDate)
  const handleSortChange = (
    sortField: string,
    sortDirection: 'asc' | 'desc' | null
  ) => {
    // If sortDirection is null, reset to default (ReportedDate desc)
    if (sortDirection === null) {
      const defaultSort = {
        key: ClaimSortKey.ReportedDate,
        order: SortOrder.Desc,
      };
      setStoredSort(defaultSort);
      setCurrentClaimSort(defaultSort);
      return;
    }

    const claimSortKeys: Record<string, ClaimSortKey> = {
      referenceId: ClaimSortKey.ReferenceId,
      insured: ClaimSortKey.InsuredPrimaryLegalName,
      claimant: ClaimSortKey.ClaimantMetaName,
      claimHandler: ClaimSortKey.ClaimHandlerUsername,
      brokerageName: ClaimSortKey.BrokeragePrimaryLegalName,
      reportedDate: ClaimSortKey.ReportedDate,
      lossDate: ClaimSortKey.LossDate,
      status: ClaimSortKey.Status,
      dueDate: ClaimSortKey.DueDate,
      tasksCount: ClaimSortKey.PendingTasksCount,
    };

    const sortConfig = {
      key: claimSortKeys[sortField] || ClaimSortKey.CreatedAt,
      order: sortDirection === 'desc' ? SortOrder.Desc : SortOrder.Asc,
    };

    setStoredSort(sortConfig);
    setCurrentClaimSort(sortConfig);
  };

  const columns: TableColumn<ClaimRowData>[] = [
    createTextColumn<ClaimRowData>('referenceId', 'CLAIM NUMBER', {
      sortable: true,
      flex: isAllClaims ? 1.8 : flexValuesClaimsExpanded[0],
      render: (value, row) => (
        <a
          href={ConvergeNavigationUrls.claimEntityDetail({
            claimEntityUuid: row.uuid,
          })}
          style={{
            color: 'black',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          className="table-cell claim-number"
        >
          {String(value)}
        </a>
      ),
    }),
    createTextColumn<ClaimRowData>('insured', 'INSURED', {
      sortable: true,
      flex: flexValuesClaimsExpanded[1],
      align: 'left',
      render: (value) => {
        const textValue = String(value ?? '\u2014');
        const maxLength = textLengths.insured;
        const truncatedText =
          textValue.length > maxLength
            ? textValue.substring(0, maxLength) + '...'
            : textValue;

        return (
          <Typography
            className="table-cell"
            sx={{
              textAlign: 'left',
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'wrap',
              textOverflow: 'ellipsis',
            }}
            title={textValue}
          >
            {truncatedText}
          </Typography>
        );
      },
    }),
    createTextColumn<ClaimRowData>('claimant', 'CLAIMANT', {
      sortable: true,
      flex: flexValuesClaimsExpanded[2],
      align: 'left',
      render: (value) => {
        const claimantName = String(value ?? '\u2014');
        const truncatedName =
          claimantName.length > 16
            ? claimantName.substring(0, 16) + '...'
            : claimantName;
        return (
          <Typography
            className="table-cell"
            title={claimantName}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {truncatedName}
          </Typography>
        );
      },
    }),
    createTextColumn<ClaimRowData>('claimHandler', 'CLAIM HANDLER', {
      sortable: true,
      align: 'left',
      flex: flexValuesClaimsExpanded[3],
      render: (value, row) => (
        <ClaimHandlerField
          claimUuid={row.uuid}
          currentHandler={String(value ?? '\u2014')}
          userUuid={row.claimHandlerUuid}
        />
      ),
    }),
    // Add brokerage column only for All Claims view
    ...(isAllClaims ? [brokerageColumn] : []),
    // Use either status or tasksCount column based on isAllClaims prop

    createTextColumn<ClaimRowData>('reportedDate', 'REPORTED', {
      sortable: true,
      align: 'left',
      flex: flexValuesClaimsExpanded[4],
      render: (value) => {
        if (!value || value === '-' || value === '—') {
          return renderTableCell('—');
        }
        const formattedDate = displayFormatDateUTC(value as string);
        return renderTableCell(
          formattedDate === 'Invalid date' ? '—' : formattedDate ?? '—'
        );
      },
    }),
    createTextColumn<ClaimRowData>('lossDate', 'DATE OF LOSS', {
      sortable: true,
      align: 'left',
      flex: flexValuesClaimsExpanded[5],
      render: (value) => {
        if (!value || value === '-' || value === '—') {
          return renderTableCell('—');
        }
        const formattedDate = displayFormatDateUTC(value as string);
        return renderTableCell(
          formattedDate === 'Invalid date' ? '—' : formattedDate ?? '—'
        );
      },
    }),
    isAllClaims ? statusColumn : tasksCountColumn,

    // Add due date column only for claim queue view (NOT All Claims view)
    ...(!isAllClaims
      ? [
          createTextColumn<ClaimRowData>('dueDate', 'DUE', {
            sortable: true,
            flex: flexValuesClaimsExpanded[7],
            render: (value) => {
              const rawFormattedDate = displayFormatDateUTC(value as string);
              const formattedDate =
                rawFormattedDate === 'Invalid date'
                  ? '\u2014'
                  : rawFormattedDate ?? '\u2014';
              const isOverdue = isDateOverdue(
                value as string,
                'main-row-due-date'
              );

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // gap: '4px',
                  }}
                >
                  <Typography
                    className={`table-cell ${
                      isOverdue ? 'overdue-date-task' : ''
                    }`}
                    sx={{
                      color: isOverdue ? '#D32F2F !important' : 'inherit',
                      fontWeight: '600 !important',
                      fontSize: '14px !important',
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    {formattedDate}
                  </Typography>
                  {isOverdue && (
                    <ErrorIcon
                      sx={{
                        color: '#D32F2F',
                        fontSize: '16px',
                      }}
                    />
                  )}
                </Box>
              );
            },
          }),
        ]
      : []),
  ];
  // todo : eventully this code return , because we need sorting

  return (
    <>
      <Box className="claim-list" sx={{ width: '100%', position: 'relative' }}>
        <ClaimsFilter
          open={filterOpen}
          toggleFilter={toggleFilter}
          currentFilters={currentFilters}
          setCurrentFilters={setCurrentFilters}
          currentFilterLabels={currentFilterLabels}
          setCurrentFilterLabels={setCurrentFilterLabels}
          filterLabels={filterLabels}
          setFilterLabels={setFilterLabels}
          setClaimFilters={setClaimFilters}
          resetPageNumber={resetPageNumber}
          filterOptions={dynamicFilterOptions}
          isAllClaims={isAllClaims}
          isPersonalClaim={!isAllClaims}
        />
        <CustomizedTable<ClaimRowData>
          data={claims || []}
          maxHeight="calc(80vh - 18vh)"
          columns={columns}
          className={isAllClaims ? 'all-claims-table' : undefined}
          enableSorting={true}
          enablePagination={true}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          defaultRowsPerPage={pageSize}
          loading={loading}
          onPageChange={(pageNumber: number) => {
            setCurrentPage(pageNumber);
            localStorage.setItem(pageNumberKey, pageNumber?.toString());
          }}
          onPageSizeChange={(newPageSize: number) => {
            setPageSize(newPageSize);
            localStorage.setItem('claimPageSize', newPageSize?.toString());
          }}
          onSortChange={(columnId, sortOrder) =>
            handleSortChange(String(columnId), sortOrder)
          }
          onSortReset={() => {
            const defaultSort = {
              key: ClaimSortKey.ReportedDate,
              order: SortOrder.Desc,
            };
            setStoredSort(defaultSort);
            setCurrentClaimSort(defaultSort);
          }}
          currentSortColumn={(() => {
            const reverseFieldMap: Record<ClaimSortKey, string> = {
              [ClaimSortKey.ReferenceId]: 'referenceId',
              [ClaimSortKey.InsuredPrimaryLegalName]: 'insured',
              [ClaimSortKey.ClaimantCompanyName]: 'claimantCompanyName',
              [ClaimSortKey.ClaimantMetaName]: 'claimant',
              [ClaimSortKey.ClaimHandlerUsername]: 'claimHandler',
              [ClaimSortKey.BrokeragePrimaryLegalName]: 'brokerageName',
              [ClaimSortKey.ReportedDate]: 'reportedDate',
              [ClaimSortKey.LossDate]: 'lossDate',
              [ClaimSortKey.Status]: 'status',
              [ClaimSortKey.DueDate]: 'dueDate',
              [ClaimSortKey.PendingTasksCount]: 'tasksCount',
              [ClaimSortKey.CreatedAt]: 'createdAt',
              [ClaimSortKey.UpdatedAt]: 'updatedAt',
              [ClaimSortKey.Uuid]: 'uuid',
            };
            return reverseFieldMap[
              currentClaimSort.key || ClaimSortKey.ReportedDate
            ] as keyof ClaimRowData;
          })()}
          currentSortOrder={
            currentClaimSort.order === SortOrder.Desc ? 'desc' : 'asc'
          }
          currentPage={currentPage}
          totalItems={data?.claims?.pageInfo?.totalResults}
          expandableRows={true}
          getRowId={(row) => row.id}
          showExpandIcon={(row) => {
            // Always show expand icon when isAllClaims is false (for task list view)
            if (!isAllClaims) {
              return true;
            }

            // For isAllClaims=true, don't show expand icon since we removed mock related claims
            // This can be updated when real related claims data is available
            return false;
          }}
          expandedContent={(rowId, row) => {
            // Show Tasks List when isAllClaims is false
            if (!isAllClaims) {
              // Find the claim data
              const claimData = claims?.find(
                (c) => String(c.id) === String(rowId)
              );

              if (!claimData) {
                return null;
              }

              return (
                <Box
                  sx={{
                    backgroundColor: 'white',
                  }}
                >
                  <ExpandedTaskRow
                    parentClaim={{
                      uuid: claimData.uuid || String(rowId),
                      referenceId: claimData.referenceId || '',
                      insured: claimData.insured || '',
                      claimant: claimData.claimant || '',
                      createdAt: claimData.createdAt || '',
                    }}
                    tasks={claimData.tasks || []}
                  />
                </Box>
              );
            }

            // For now, return null since we removed mock related claims
            // This can be updated when real related claims data is available
            return null;
          }}
        />
      </Box>
    </>
  );
};
